import { ref, type Ref } from "vue";

/**
 * マウス/タッチ共通の長押し検出コンポーザブル。
 *
 * 配置場所についての補足:
 *   本コンポーザブルは「担当外ルーム」等の業務知識を一切持たない汎用ロジックのため、
 *   feature配下ではなく共通 composables 直下に配置する。
 */

/** 長押しとみなすしきい値（0.9秒未満は不発、1.0秒以上で成功） */
export const LONG_PRESS_DURATION_MS = 1000;

/** タッチ操作でキャンセルとみなす移動距離のしきい値（px） */
export const LONG_PRESS_MOVE_THRESHOLD_PX = 10;

export type UseLongPressOptions = {
  /** 長押し判定のしきい値（ms）。省略時は LONG_PRESS_DURATION_MS */
  duration?: number;
  /** タッチ移動によるキャンセルのしきい値（px）。省略時は LONG_PRESS_MOVE_THRESHOLD_PX */
  moveThreshold?: number;
};

export type UseLongPressBind = {
  onMousedown: () => void;
  onMouseup: () => void;
  onMouseleave: () => void;
  onTouchstart: (event: TouchEvent) => void;
  onTouchend: () => void;
  onTouchcancel: () => void;
  onTouchmove: (event: TouchEvent) => void;
};

export type UseLongPressReturn = {
  /** テンプレートに `v-bind="longPress.bind"` として展開するイベントハンドラ群 */
  bind: UseLongPressBind;
  /** 経過割合（0〜1）。Could Have: 将来のプログレスバー表示用に公開 */
  progressRatio: Ref<number>;
};

/**
 * @param onLongPressComplete しきい値到達時にのみ呼ばれるコールバック
 * @param options しきい値等のオプション
 */
export function useLongPress(
  onLongPressComplete: () => void,
  options: UseLongPressOptions = {}
): UseLongPressReturn {
  const duration = options.duration ?? LONG_PRESS_DURATION_MS;
  const moveThreshold = options.moveThreshold ?? LONG_PRESS_MOVE_THRESHOLD_PX;

  const progressRatio = ref(0);

  let timerId: ReturnType<typeof setTimeout> | null = null;
  let touchStartX = 0;
  let touchStartY = 0;

  const cancel = (): void => {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
    progressRatio.value = 0;
  };

  const start = (): void => {
    cancel();
    timerId = setTimeout(() => {
      progressRatio.value = 1;
      timerId = null;
      onLongPressComplete();
    }, duration);
  };

  const onMousedown = (): void => start();
  const onMouseup = (): void => cancel();
  const onMouseleave = (): void => cancel();

  const onTouchstart = (event: TouchEvent): void => {
    const touch = event.touches[0];
    touchStartX = touch?.clientX ?? 0;
    touchStartY = touch?.clientY ?? 0;
    start();
  };
  const onTouchend = (): void => cancel();
  const onTouchcancel = (): void => cancel();
  const onTouchmove = (event: TouchEvent): void => {
    // 長押し中でなければ何もしない
    if (timerId === null) return;

    const touch = event.touches[0];
    if (!touch) return;

    const dx = touch.clientX - touchStartX;
    const dy = touch.clientY - touchStartY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // タッチ開始位置から一定距離以上動いた場合はスクロール等の意図しない操作とみなしキャンセルする
    if (distance >= moveThreshold) {
      cancel();
    }
  };

  const bind: UseLongPressBind = {
    onMousedown,
    onMouseup,
    onMouseleave,
    onTouchstart,
    onTouchend,
    onTouchcancel,
    onTouchmove,
  };

  return { bind, progressRatio };
}
